import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Logo } from '../components/Logo';
import { toast } from 'sonner';
import { Phone, Mail, Lock, Loader2, ArrowRight } from 'lucide-react';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-10" data-testid="login-logo">
            <Logo size="large" />
          </div>

          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Welcome back</h1>
            <p className="text-slate-500">Sign in to your account to continue</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-12 h-12 border-2 focus:border-teal-500"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-12 h-12 border-2 focus:border-teal-500"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>
            <Button 
              type="submit" 
              className="w-full h-12 bg-teal-600 hover:bg-teal-700 shadow-lg shadow-teal-200 text-base font-semibold" 
              disabled={loading} 
              data-testid="login-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <>
                  Sign In
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </form>

          <div className="flex items-center justify-between mt-4">
            <Link to="/forgot-password" className="text-sm text-teal-600 hover:text-teal-700 font-medium" data-testid="forgot-password-link">
              Forgot password?
            </Link>
          </div>

          <p className="text-center text-slate-500 mt-6">
            Don't have an account?{' '}
            <Link to="/?lead=1" className="text-teal-600 hover:text-teal-700 font-semibold">
              Contact our team
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Visual */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-teal-500 to-cyan-600 items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-teal-400 rounded-full blur-3xl opacity-30"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-400 rounded-full blur-3xl opacity-30"></div>
        
        <div className="relative max-w-lg text-center">
          <div className="w-20 h-20 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center mx-auto mb-8">
            <Phone className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-4xl font-bold text-white mb-6">
            Your AI receptionist is waiting
          </h2>
          <p className="text-xl text-teal-100 leading-relaxed">
            Access your dashboard for calls and omnichannel messages (SMS, WhatsApp, Facebook), transcripts, sentiment,
            smart next steps, and agent management.
          </p>
        </div>
      </div>
    </div>
  );
}

export function AuthCallbackPage() {
  const { exchangeSession } = useAuth();
  const navigate = useNavigate();
  const [processed, setProcessed] = useState(false);

  // Process session_id from URL hash
  if (!processed && window.location.hash?.includes('session_id=')) {
    setProcessed(true);
    const hash = window.location.hash.substring(1);
    const params = new URLSearchParams(hash);
    const sessionId = params.get('session_id');

    if (sessionId) {
      exchangeSession(sessionId)
        .then(() => {
          toast.success('Signed in successfully!');
          navigate('/dashboard', { replace: true });
        })
        .catch((error) => {
          toast.error('Authentication failed');
          navigate('/login', { replace: true });
        });
    } else {
      navigate('/login', { replace: true });
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-50 to-white">
      <div className="text-center">
        <div className="w-16 h-16 rounded-2xl bg-violet-100 flex items-center justify-center mx-auto mb-6">
          <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
        </div>
        <h2 className="text-xl font-semibold text-slate-900 mb-2">Completing sign in...</h2>
        <p className="text-slate-500">Please wait a moment</p>
      </div>
    </div>
  );
}
